export type WordResponseType = "review" | "outline" | "text";

export interface ParsedWordResponse {
  type: WordResponseType;
  count: number;
}

export interface ReviewChange {
  original: string;
  replacement: string;
}

/** One row of the specification's outline contract (§4.1). */
export interface OutlineSection {
  heading: string;
  /** "title" is a distinct Word style, NOT Heading 1 — see insertWordOutline. */
  level: "title" | 1 | 2 | 3;
  stub: string;
}

/**
 * Outcome of looking for the outline payload in a reply.
 *
 * Three states, not two, and the third is what AC-5 turns on: a reply that
 * carries a payload we could not parse must fall back to Variant C, whereas a
 * reply with no payload at all is simply prose. Collapsing "malformed" into
 * "absent" would let a broken payload render a button built from nothing.
 */
export type OutlineParse =
  | { status: "none" }
  | { status: "malformed" }
  | { status: "ok"; sections: OutlineSection[] };

// The payload may arrive fenced (```json … ```) or bare. Both are matched from
// the LAST opening brace that begins an "outline" key, so prose mentioning the
// word "outline" earlier in the reply cannot start a false match.
const OUTLINE_PAYLOAD_RE = /```(?:json)?\s*(\{[\s\S]*?"outline"[\s\S]*?\})\s*```|(\{\s*"outline"\s*:[\s\S]*\})/;
/** Cheap "a payload was attempted" probe — see the truncation note below. */
const OUTLINE_MARKER_RE = /\{\s*"outline"\s*:/;

function coerceLevel(raw: unknown): OutlineSection["level"] | null {
  if (raw === "title") return "title";
  const n = typeof raw === "string" ? Number(raw) : raw;
  return n === 1 || n === 2 || n === 3 ? n : null;
}

/**
 * Extract the `{"outline":[…]}` payload the Word skills emit (§4.1, TO-02).
 *
 * Every row must carry a usable heading and a level the contract allows; a row
 * that does not is treated as a malformed payload rather than being skipped.
 * Silently dropping rows would insert a DIFFERENT outline from the one the
 * count on the button promised, which is the kind of quiet mismatch G-17 exists
 * to prevent elsewhere in this channel.
 */
export function parseOutlineFromAi(text: string): OutlineParse {
  if (!text) return { status: "none" };
  const match = OUTLINE_PAYLOAD_RE.exec(text);
  if (!match) {
    // The payload regex needs a closing brace, so a TRUNCATED payload — the
    // model cut off mid-emit, or the stream dropped — would otherwise read as
    // "no payload at all" and let the markdown heuristic build a button from
    // whatever headings happened to be above it. A started-but-unfinished
    // payload is malformed, which is the case AC-5 sends to Variant C.
    return OUTLINE_MARKER_RE.test(text) ? { status: "malformed" } : { status: "none" };
  }
  const raw = match[1] ?? match[2];
  if (!raw) return { status: "malformed" };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { status: "malformed" };
  }
  const rows = (parsed as { outline?: unknown })?.outline;
  if (!Array.isArray(rows) || rows.length === 0) return { status: "malformed" };

  const sections: OutlineSection[] = [];
  for (const row of rows) {
    const r = row as { heading?: unknown; level?: unknown; stub?: unknown };
    const heading = typeof r?.heading === "string" ? r.heading.trim() : "";
    const level = coerceLevel(r?.level);
    if (!heading || level === null) return { status: "malformed" };
    sections.push({
      heading,
      level,
      stub: typeof r?.stub === "string" ? r.stub : "",
    });
  }
  return { status: "ok", sections };
}

/**
 * One heading line: legal numbering, then the text.
 *
 * A SINGLE pattern rather than one per level, and deliberately unambiguous.
 * The earlier form matched each depth separately with `[.\s]\s*(.+)` — where
 * `\s*` and `.` can both consume a space, so a long run of whitespace made the
 * engine retry every split point and cost O(n²) per line. Here `[ \t]+` is
 * followed by `\S`, which pins where the separator ends and leaves nothing to
 * backtrack over.
 */
const OUTLINE_HEADING_RE = /^(\d+(?:\.\d+){0,2})\.?[ \t]+(\S.*)$/;
/** A bracketed guidance line, e.g. "[Define key terms]". */
const OUTLINE_STUB_RE = /^\[[^\]]*\]$/;

/** Depth implied by the numbering: "2" -> 1, "2.1" -> 2, "2.1.1" -> 3. */
function levelFromNumbering(numbering: string): 1 | 2 | 3 {
  const depth = numbering.split(".").length;
  if (depth >= 3) return 3;
  return depth === 2 ? 2 : 1;
}

/**
 * Recover an outline's structure from its PLAIN TEXT form.
 *
 * The last resort, and in practice the one that fires. TO-02 was implemented
 * twice on the assumption the model would hand the structure over deliberately
 * — first as a JSON payload in the reply, then as a pill_word_insert_outline
 * tool call. It did neither: across three prompt revisions it wrote a
 * confirmation sentence and called pill_word_copy_text, whose `text` argument
 * contained the complete outline all along.
 *
 * So the structure is read back out of that text. Levels come from the legal
 * numbering the outline already uses — "2." is a section, "2.1" a sub-section,
 * "2.1.1" below that — and a leading unnumbered line is the document Title.
 * `[Square Brackets]` lines attach to the heading above them as stubs.
 */
export function parseOutlineTextToSections(text: string): OutlineSection[] {
  if (!text) return [];
  const sections: OutlineSection[] = [];

  /** Guidance for the heading above; the first stub wins. */
  const attachStub = (line: string): void => {
    const last = sections[sections.length - 1];
    if (last && !last.stub) last.stub = line;
  };

  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const heading = OUTLINE_HEADING_RE.exec(line);
    if (heading) {
      sections.push({ heading: line, level: levelFromNumbering(heading[1]!), stub: "" });
    } else if (sections.length === 0 && !OUTLINE_STUB_RE.test(line)) {
      // The first unnumbered, unbracketed line is the document title.
      sections.push({ heading: line, level: "title", stub: "" });
    } else {
      // Bracketed guidance, or unnumbered prose after headings began (a
      // subtitle or note). Attached rather than given an invented heading level.
      attachStub(line);
    }
  }

  // Two entries is a heading and a subtitle, not an outline. Require enough
  // structure that "Insert outline (N)" is an honest description.
  return sections.filter((s) => s.level !== "title").length >= 3 ? sections : [];
}

/** One row of the review contract (§4.1). */
export interface ReviewFinding {
  /** Verbatim slice of the document the finding is anchored to. Everything
   *  downstream is located by searching for exactly this. */
  matchText: string;
  /** What is wrong — becomes the body of the comment left on the match. */
  problem: string;
  /** Proposed wording. Absent means the finding is raised for the reviewer to
   *  judge, so it lands as a comment with no tracked change. */
  suggestion?: string;
  /** Anchor by exact case — set for terminology findings, whose wrong form may
   *  differ from the right one only by capitalisation. */
  matchCase?: boolean;
  /** Fix every occurrence of matchText, not just one — set for terminology
   *  findings, which name a form rather than a place. */
  allOccurrences?: boolean;
}

/**
 * Outcome of looking for the review payload in a reply.
 *
 * FOUR states, not three, and the extra one is what AC-3 turns on: a clean
 * document returns `{"review":[]}`, which is a real answer ("I looked, there is
 * nothing") and not a broken payload. Folding it into "malformed" would send a
 * clean review down the fallback path, and folding it into "ok" would render
 * "Apply review (0)". Both are wrong in the same place — the empty list has to
 * be its own outcome so the bar can simply not appear.
 */
export type ReviewParse =
  | { status: "none" }
  | { status: "malformed" }
  | { status: "clean" }
  | { status: "ok"; findings: ReviewFinding[] };

/** Cheap "a payload was attempted" probe — catches a truncated emit. */
const REVIEW_MARKER_RE = /\{\s*"review"\s*:/;

/** What the display text becomes when an EMPTY review payload was the whole
 *  reply. Stripping it left nothing, and an empty bubble is reported as
 *  "Sorry — I couldn't complete that" by the backend's empty-turn floor
 *  (AIVA-7724) — an apology for the commonest correct outcome there is. The
 *  payload already said what happened; this is the same statement in words. */
const REVIEW_CLEAN_ONLY_REPLY = "I checked the selected text and found nothing to flag.";

/**
 * Locate the `{"review":…}` object by MATCHING BRACES, not by regex.
 *
 * The regex this replaces ran `\{\s*"review"\s*:[\s\S]*\}` — greedy to the last
 * `}` anywhere in the reply. So the moment any prose followed the payload and
 * happened to contain a brace, the captured span ran past the object's own end,
 * `JSON.parse` threw, and the payload was declared malformed: EVERY finding
 * lost, from one stray character. Content-dependent, which is why it read as
 * the command being inconsistent rather than as a parse bug (AIVA-7722 — the
 * repeatability ticket; the empty-payload half below is AIVA-7724).
 *
 * The same regex also drove `stripWordPayloads`, where over-capturing deleted
 * the user-visible answer along with the payload.
 *
 * Brace matching has no such failure mode: it stops at the object's real end.
 * Quote- and escape-aware, because `matchText` is a whole verbatim sentence and
 * may contain braces or escaped quotes of its own. A code fence needs no
 * special case — scanning from the marker's own `{` ignores the fence around it.
 *
 * Returns null for an unterminated object, which is a genuinely truncated emit.
 */
export function extractReviewPayload(text: string): string | null {
  const marker = REVIEW_MARKER_RE.exec(text);
  const start = marker ? text.indexOf("{", marker.index) : -1;
  if (start < 0) return null;
  const end = matchingBraceIndex(text, start);
  return end < 0 ? null : text.slice(start, end + 1);
}

/**
 * Index of the `}` that closes the `{` at `from`, or -1 if it never closes.
 *
 * Its own function so the scan stays a plain state machine — one concern per
 * branch — rather than another level of nesting inside the caller.
 *
 * String-aware, and that is the point: `matchText` is a whole verbatim sentence
 * and may hold braces or escaped quotes, so a brace inside a string must not
 * change the depth.
 */
function matchingBraceIndex(text: string, from: number): number {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = from; i < text.length; i++) {
    const ch = text[i];
    if (escaped) {
      escaped = false;
    } else if (inString) {
      if (ch === "\\") escaped = true;
      else if (ch === '"') inString = false;
    } else if (ch === '"') {
      inString = true;
    } else if (ch === "{") {
      depth++;
    } else if (ch === "}" && --depth === 0) {
      return i;
    }
  }
  return -1;
}

/**
 * Extract the `{"review":[…]}` payload the Word review skills emit (§4.1).
 *
 * Every row must carry the `matchText` the finding is anchored to and the
 * `problem` that becomes its comment; a row missing either makes the payload
 * MALFORMED rather than being skipped. Silently dropping one would put a count
 * on the button that does not match the findings behind it — RV-01's own
 * failure, arriving one step before the apply.
 */
/** Validate the payload's rows, or null when ANY row is unusable — one bad row
 *  makes the whole payload malformed rather than being skipped, so the count on
 *  the button can never disagree with the findings behind it. */
function reviewRowsToFindings(rows: readonly unknown[]): ReviewFinding[] | null {
  const findings: ReviewFinding[] = [];
  for (const row of rows) {
    const r = row as { matchText?: unknown; problem?: unknown; suggestion?: unknown };
    const matchText = typeof r?.matchText === "string" ? r.matchText.trim() : "";
    const problem = typeof r?.problem === "string" ? r.problem.trim() : "";
    if (!matchText || !problem) return null;
    const suggestion = typeof r?.suggestion === "string" ? r.suggestion : "";
    findings.push({ matchText, problem, ...(suggestion.trim() ? { suggestion } : {}) });
  }
  return findings;
}

export function parseReviewSuggestions(text: string): ReviewParse {
  if (!text) return { status: "none" };
  const raw = extractReviewPayload(text);
  if (!raw) {
    return REVIEW_MARKER_RE.test(text) ? { status: "malformed" } : { status: "none" };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { status: "malformed" };
  }
  const rows = (parsed as { review?: unknown })?.review;
  if (!Array.isArray(rows)) return { status: "malformed" };
  if (rows.length === 0) return { status: "clean" };

  const findings = reviewRowsToFindings(rows);
  return findings ? { status: "ok", findings } : { status: "malformed" };
}

/** A numbered defined-term finding: `1. **Term: "X"** — Used as "Y" in one place
 *  and "Z" in another. Recommendation: use "W" throughout.` */
// `[\s*]*` as ONE class rather than `\s*\**\s*`: the three-part form lets the
// engine split a run of spaces and asterisks many ways, which is the
// super-linear backtracking Sonar flags. A single class has one way to match.
// `[\s*]*` on BOTH sides of "Term:" — the model closes its bold before the
// quote as often as after it (`**Term:** "X"` vs `**Term: "X"**`), and a list
// this parser cannot read renders no Apply review bar at all, so the same
// /terms run showed a different set of buttons from one reply to the next.
const TERM_FINDING_RE = /^\s*\d+\.[\s*]*Term:[\s*]*"([^"]+)"/;
/** The preferred form, when the recommendation names one in quotes. */
const RECOMMENDATION_RE = /Recommendation:[^"]*"([^"]+)"/;

/**
 * Recover review findings from the findings list itself.
 *
 * The last resort, and in practice the one that fires — the same situation
 * `parseOutlineTextToSections` was written for. The review skills write a
 * structured findings list and then call a per-term `Replace all` pill instead
 * of emitting the payload, so the bar shows one button per form whose count is
 * that form's OCCURRENCE count. Three inconsistencies then render as a button
 * reading "(1)", which answers a question the user never asked.
 *
 * The list is machine-readable enough to recover: the term, the variants it was
 * used as, and the recommended form are all quoted.
 *
 * ONE entry per numbered finding, so the count on the button is the number of
 * inconsistencies the user just read. A term used in two wrong ways is still one
 * finding — the extra variants are named in the comment rather than split into
 * separate entries, because a button reading "(4)" under a list of three reads
 * as a miscount even when it is arithmetically defensible.
 *
 * A finding whose recommendation names no quoted form ("use a single standard
 * list structure throughout") is real but not mechanically applicable, so it
 * becomes a comment on the term with no suggestion — raised for the reviewer to
 * judge rather than silently dropped from the count.
 */
/**
 * A numbered review finding: `1. "flagged phrase" → Active rewrite: "better"`.
 *
 * The shape the /passive, /clarity and /citations prompts specify for their
 * findings list. Both halves are quoted, so the list is machine-readable even
 * when the model writes it and then forgets the pill.
 *
 * The label between the arrow and the rewrite varies with the skill ("Active
 * rewrite", "Suggested rewrite", plain "Rewrite"), and the arrow arrives as
 * either a real → or an ASCII ->, so both are accepted. Anchored to the line
 * start after the number so prose that merely contains a quoted pair cannot
 * masquerade as a finding.
 */
const REVIEW_LIST_LEAD_RE = /^\s*\d+\.\s*"([^"]+)"\s*(?:\u2192|->)\s*/;
const REVIEW_LIST_REWRITE_RE = /^(?:active\s+|suggested\s+)?rewrite\s*(?::\s*)?"([^"]*)"/i;

/** Both halves of a numbered finding line, or null. Two patterns rather than one: the full shape was past the regex complexity limit. */
function matchReviewListLine(line: string): [string, string] | null {
  const lead = REVIEW_LIST_LEAD_RE.exec(line);
  if (!lead) return null;
  const rewrite = REVIEW_LIST_REWRITE_RE.exec(line.slice(lead[0].length));
  return rewrite ? [lead[1]!, rewrite[1] ?? ""] : null;
}

/**
 * Recover review findings from a `"phrase" → rewrite: "better"` list.
 *
 * AIVA-7722. The review skills are required to call
 * `pill_word_apply_review_edits` after writing their list, and repeatedly did
 * not: the user read four correct findings with no highlight to locate them by
 * and no button to apply them. Three prompt revisions failed to make the call
 * reliable, so the bar is rebuilt from the list instead.
 *
 * COMMENT-ONLY, deliberately. `matchText` is the quoted phrase, which is
 * verbatim document text and anchors exactly. The rewrite is NOT used as
 * `suggestion`: it was written against the phrase in isolation, and pairing a
 * phrase-level original with a clause-level replacement produces a broken
 * sentence — "have been outlined herein" → "the document outlines" turns "The
 * risks have been outlined herein" into "The risks the document outlines". So
 * the wording rides in the comment, where the reviewer applies it by judgement.
 * A fallback may locate a problem on its own authority; it may not rewrite the
 * document on one.
 */
export function parseReviewListFindings(text: string): ReviewFinding[] {
  if (!text) return [];
  const findings: ReviewFinding[] = [];
  const seen = new Set<string>();
  for (const raw of text.split("\n")) {
    const match = matchReviewListLine(raw.trim());
    if (!match) continue;
    const matchText = match[0].trim();
    const rewrite = match[1].trim();
    if (!matchText) continue;
    // One entry per distinct phrase: a repeated quote in the list would
    // otherwise inflate the count on the button past what the user just read.
    const key = matchText.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    findings.push({
      matchText,
      problem: rewrite
        ? `Passive voice. Suggested active rewrite: "${rewrite}"`
        : "Passive voice.",
    });
  }
  return findings;
}

export function parseReviewFindingsFromText(text: string): ReviewFinding[] {
  if (!text) return [];
  const findings: ReviewFinding[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const termMatch = TERM_FINDING_RE.exec(line);
    if (!termMatch) continue;
    const term = termMatch[1]!;
    const problem = line.replace(/^\s*\d+\.\s*/, "").replace(/\*\*/g, "").trim();
    const preferred = RECOMMENDATION_RE.exec(line)?.[1]?.trim() ?? "";

    // Every quoted string after the term and before the recommendation is a
    // variant the document actually contains.
    const beforeRecommendation = line.split(/Recommendation:/)[0] ?? line;
    const variants = [...beforeRecommendation.matchAll(/"([^"]+)"/g)]
      .map((m) => m[1]!.trim())
      .filter((v) => v && v !== term);

    if (!preferred) {
      findings.push({ matchText: term, problem });
      continue;
    }
    const wrongForms = [...new Set(variants)].filter((v) => v !== preferred);
    if (wrongForms.length === 0) {
      findings.push({ matchText: term, problem });
      continue;
    }
    // One finding PER wrong form. The earlier shape rewrote the first form and
    // only NAMED the rest in its comment, so a form the review mentioned was a
    // form the Apply button never fixed. Case-sensitive and every occurrence,
    // because a terminology finding names a form rather than a place: "receiving
    // party" must not match the correct "Receiving Party", and its second
    // occurrence is as wrong as its first (see ReviewEditFinding).
    for (const wrong of wrongForms) {
      findings.push({ matchText: wrong, problem, suggestion: preferred, matchCase: true, allOccurrences: true });
    }
  }
  return findings;
}

/**
 * Remove both structured payloads from text destined for the chat bubble (AC-4).
 *
 * Each contract asks the model for a short confirmation PLUS the JSON. The
 * confirmation is the message; the JSON is machinery, and a user seeing it
 * would reasonably conclude the add-in had malfunctioned.
 *
 * One function for both because it has one caller and one rule — a per-contract
 * stripper would only give the chat bubble a way to render one payload while
 * hiding the other.
 */
/**
 * A leading heading that merely opens a NEW NUMBERED SECTION, or "" if there is
 * none.
 *
 * "## 100.3 Exceptions and Dependencies", "100.3 Exceptions and Dependencies"
 * and "### 4.2 Scope" all match. "## Summary" does not: an unnumbered title is
 * a heading the reply meant to write, not the document's architecture being
 * extended.
 */
/*
 * `#{0,6}` used to sit between two `\s*` runs, and with zero hashes those two
 * runs became adjacent — a whitespace-only line could then be split between
 * them n different ways, all of which fail at `\d+`, so rejecting one cost
 * O(n^2) (Sonar S5852). Making the hash group optional AS A WHOLE puts a
 * mandatory `#` between the two runs whenever both exist, so there is exactly
 * one way to match any input. The language it accepts is unchanged.
 *
 * The trailing `.*$` went with it: this is only ever `.test()`ed, and `.*`
 * after a matched `\S` can never fail.
 */
const INVENTED_SECTION_HEADING_RE = /^\s*(?:#{1,6}\s*)?\d+(?:\.\d+)+\.?\s+\S/;

/**
 * Remove an invented numbered section heading from the top of placement text.
 *
 * AIVA-7938, and the fifth report of it. /write kept opening its answer with a
 * heading that continued the document's own numbering — "## 100.3 Exceptions and
 * Dependencies" above the paragraphs that were actually asked for. The prompt
 * forbids it twice over and five revisions have not made it hold, so the text is
 * cleaned here instead: the user asked for content, and a section heading is
 * architecture.
 *
 * Narrow on purpose:
 *   - only the FIRST line is examined, so a heading deeper in a long insert —
 *     which was put there deliberately — survives;
 *   - only a NUMBERED heading is removed, so "## Summary" and any other titled
 *     block a skill means to write is left alone;
 *   - if stripping would leave nothing, the text is returned untouched. A reply
 *     that is ONLY a heading is a different problem, and silently emptying the
 *     pill would be worse than placing it.
 */
export function stripInventedSectionHeading(text: string): string {
  const lines = text.split(/\r?\n/);
  const first = lines[0] ?? "";
  if (!INVENTED_SECTION_HEADING_RE.test(first)) return text;
  const rest = lines.slice(1).join("\n").replace(/^\s*\n/, "");
  return rest.trim() ? rest : text;
}

/**
 * Drop bubble lines that merely repeat a heading the outline card is showing.
 *
 * `/structure` and `/outline` READ MODE both render the document's headings as a
 * card, computed in the add-in. A prompt that ALSO asks the model to type the
 * list produces the same 171 headings twice, a few pixels apart — the model's
 * copy being the unreliable one, since it truncates.
 *
 * The prompts no longer ask for it, but a prompt lives in Cosmos and a stale one
 * keeps transcribing until it is re-seeded. This is the client-side half, so the
 * pane is right regardless of which prompt is deployed.
 *
 * Removal is MATCHED, never guessed. A line goes only when it is a list item
 * whose text — after its bullet and any leading enumerator — equals a heading the
 * card is already displaying. Prose is untouched, and a heading the card does
 * NOT have is untouched.
 *
 * A reply that was ONLY the list strips to "" — deliberately, and unlike
 * `stripInventedSectionHeading`'s refuse-to-empty. The caller only passes
 * `renderedHeadings` when the outline card actually renders below the bubble,
 * so an emptied bubble here means the card already says everything the reply
 * did. Returning the original instead is how the full 171-line duplication
 * came back on an all-list reply.
 */
export function stripOutlineAlreadyRendered(
  text: string,
  renderedHeadings: readonly string[] | undefined,
): string {
  if (!text || !renderedHeadings?.length) return text;

  // "- 12. Heading", "* Heading", "- **3.1** Heading" -> "heading". The
  // enumerator is stripped because the model numbers its own list independently
  // of the document's numbering, so the raw lines rarely match verbatim.
  //
  // Emphasis markers come off BEFORE the enumerator, not after: the model bolds
  // its numbers ("- **12.** Heading"), and stripping in the other order left the
  // `**` in front of the digits so the enumerator pattern never matched and the
  // line survived as a duplicate.
  const norm = (value: string) =>
    value
      .replace(/^\s*[-*+]\s+/, '')
      .replace(/[*_`]/g, '')
      .replace(/^\s*\d+[A-Za-z]?(?:\.\d+)*[.)]?\s+/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const shown = new Set(renderedHeadings.map(norm).filter(Boolean));
  const isListLine = (line: string) => /^\s*(?:[-*+]\s+|\d+[A-Za-z]?(?:\.\d+)*[.)]?\s+)/.test(line);

  const kept = text.split(/\r?\n/).filter((line) => {
    if (!line.trim() || !isListLine(line)) return true;
    return !shown.has(norm(line));
  });

  return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Remove the machine-readable payloads, leaving what the user reads.
 *
 * The review payload is cut by its matched span rather than by pattern, so
 * prose that follows it survives — a greedy match used to take the answer with
 * it (see extractReviewPayload).
 *
 * A reply that was ONLY an empty payload becomes the clean-result sentence
 * instead of "". Returning empty here is what surfaced the generic apology on a
 * document with nothing to flag, which is the defect in AIVA-7724: the model
 * did exactly as instructed — one `{"review": []}` and no tool calls — and the
 * user was told AIVA could not complete the request.
 */
/**
 * Hide a payload that is still arriving, for a reply that is mid-stream.
 *
 * `stripWordPayloads` removes a payload by its own span, and finding that span
 * needs a BALANCED object — `extractReviewPayload` returns null while the
 * closing brace has not been streamed yet, and the outline regex needs its
 * whole match. So during a stream the raw `{"review":[{"matchText":"…` text
 * renders in the bubble and then vanishes when the turn completes, which reads
 * as a glitch and breaks the contract that a user never sees the payload as
 * raw JSON (§4.1, AC-4).
 *
 * The marker probes already exist for the truncated case. The payload is always
 * emitted AFTER the prose, so cutting from the first marker to the end is both
 * sufficient and safe: there is no prose after it to lose. Any ```json fence
 * opened just before it goes too, or the bubble is left holding a dangling one.
 */
export function stripStreamingPayloads(text: string): string {
  if (!text) return text;
  const markers = [REVIEW_MARKER_RE.exec(text), OUTLINE_MARKER_RE.exec(text)].filter(
    (m): m is RegExpExecArray => m !== null
  );
  if (markers.length === 0) return text;
  let cut = Math.min(...markers.map((m) => m.index));
  // Take an immediately-preceding ```json fence with it.
  const fence = /```(?:json)?\s*$/.exec(text.slice(0, cut));
  if (fence) cut = fence.index;
  return text
    .slice(0, cut)
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
}

export function stripWordPayloads(text: string): string {
  if (!text) return text;
  const review = extractReviewPayload(text);
  const withoutReview = review ? text.replace(review, "") : text;
  const stripped = withoutReview
    .replace(OUTLINE_PAYLOAD_RE, "")
    // Cutting the object by its own span leaves the fence that wrapped it, so
    // an emptied ```json block is swept up here rather than shown as a stray
    // pair of backticks.
    // One `\s*` rather than a run of overlapping optional quantifiers: the
    // three-part form let the engine split the whitespace many ways, which is
    // the super-linear backtracking Sonar flags. `\s` already covers tabs and
    // newlines.
    .replace(/```(?:json)?\s*```/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (stripped) return stripped;
  // Nothing but a payload. An empty review is a real answer and gets said out
  // loud; anything else (a findings payload with no prose) is left empty for
  // the caller's own handling rather than described with invented copy.
  return parseReviewSuggestions(text).status === "clean" ? REVIEW_CLEAN_ONLY_REPLY : stripped;
}

/**
 * Longest a numbered line can be and still read as an outline heading.
 *
 * Deliberately generous. A real outline entry carrying a bracketed stub —
 * "3. Confidentiality Obligations [Set out the duty not to disclose]" — is
 * around ten words, while a drafted clause paragraph is thirty or more. Set it
 * too tight and a legitimate outline loses its single Insert button; the gap
 * between the two shapes is wide enough that this sits comfortably in it.
 */
const OUTLINE_ITEM_MAX_WORDS = 14;

/**
 * How many long body lines it takes to call a reply drafted content.
 *
 * Two, not one: a genuine outline may carry a single lead-in sentence above the
 * headings ("Here is a structure for the agreement:"), and one such line must
 * not cost it its Insert outline button. Two or more thirty-word paragraphs is
 * prose that happens to have headings over it.
 *
 * Applied to BOTH the heading and numbered branches below: a selection's own
 * real Word Heading-styled paragraphs get marked with `# `/`## ` for the model
 * (AIVA-7080), and an "improve this text" reply on 3+ such paragraphs echoes
 * them back over real rewritten content — the same shape a numbered list
 * opening a longer explanation produces (AIVA-7881/AIVA-7894).
 */
const MIN_PROSE_LINES_FOR_CONTENT = 2;

/**
 * Body lines long enough to be drafted prose rather than an outline stub.
 *
 * Headings and blank lines are not body. Everything else is measured with the
 * same word threshold the numbered branch uses, so the two shapes are judged by
 * one rule: an outline entry and its stub are short, a drafted clause is not.
 */
function proseBodyLines(text: string): number {
  return text.split(/\r?\n/).filter((line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (/^#{1,3} /.test(trimmed)) return false;
    return trimmed.split(/\s+/).length > OUTLINE_ITEM_MAX_WORDS;
  }).length;
}

export function parseWordResponse(text: string): ParsedWordResponse {
  if (!text) return { type: "text", count: 0 };

  // Review block: contains ~~deleted~~ **inserted** pattern (tracked changes)
  const strikethroughs = text.match(/~~[^~\n]+~~/g) ?? [];
  if (strikethroughs.length >= 1) {
    return { type: "review", count: strikethroughs.length };
  }

  // Outline block: 3+ markdown headings or 3+ numbered list items
  //
  // Headings alone are not enough, for the same reason numbering alone is not
  // (see the numbered branch below). `/write` drafting three sections returns
  // three '##' headings with a full paragraph of prose under each — that is
  // drafted CONTENT, not an outline, and classifying it as one collapses the
  // Variant C four-button placement bar to a single "Insert outline (3)"
  // (DE-01 AC-2). An outline's headings carry nothing, or a short bracketed
  // stub; drafted prose runs to thirty words and more. The guard was applied to
  // the numbered branch and not to this one.
  const headings = text.match(/^#{1,3} .+/gm) ?? [];
  if (headings.length >= 3 && proseBodyLines(text) < MIN_PROSE_LINES_FOR_CONTENT) {
    return { type: "outline", count: headings.length };
  }

  // Numbered items are only an outline when they read like HEADINGS.
  //
  // A numbered list alone is not enough: a drafted clause whose three paragraphs
  // the model happened to number 1./2./3. was classified as an outline, which
  // collapsed the four-button placement bar down to a single "Insert outline (3)"
  // — DE-01 AC-2 broken by prose that merely looked like a list.
  // An outline entry is a heading: "2.1 Scope", "Termination". A paragraph of
  // drafted prose runs to dozens of words. Length separates them cleanly, where
  // the numbering does not.
  //
  // Same MIN_PROSE_LINES_FOR_CONTENT guard as the heading branch above
  // (AIVA-7894/7881): a short numbered list opening a longer explanation — the
  // numbered lines themselves stay under the per-item threshold below, so
  // without this a normal reply that merely STARTS with a 3-item list still
  // collapsed to "Insert outline".
  const numbered = (text.match(/^\d+\. .+/gm) ?? []).filter(
    (line) => line.replace(/^\d+\.\s*/, "").trim().split(/\s+/).length <= OUTLINE_ITEM_MAX_WORDS,
  );
  if (numbered.length >= 3 && proseBodyLines(text) < MIN_PROSE_LINES_FOR_CONTENT) {
    return { type: "outline", count: numbered.length };
  }

  return { type: "text", count: 0 };
}

/**
 * Rewrite a placement label to name the structure it will insert.
 *
 * The specification's "Variant B" bar reads ``Insert outline (7)`` rather than a
 * bare ``Insert at cursor``, so the user knows how much is about to land in the
 * document before they click. ``parseWordResponse`` recognises an outline from
 * the markdown the Word skills already produce (3+ ``#`` headings or 3+
 * numbered items), so no response-format change is needed to get the count.
 *
 * Prose replies are returned unchanged — they keep the default Variant C
 * labels. Shared by the pill-tool emitters and the done-event fallback row so
 * both paths label an outline the same way.
 */
export function outlineAwareLabel(label: string, text: string): string {
  // A reply carrying a BROKEN payload keeps its plain Variant C labels. The
  // heading-count relabel is the legacy path for payload-free replies; letting
  // it fire here would put "Insert outline (3)" on a button whose 3 came from
  // counting '#' lines, not from the outline the model failed to emit — a count
  // the user would reasonably trust (AC-5).
  if (parseOutlineFromAi(text).status === "malformed") return label;
  const parsed = parseWordResponse(text);
  if (parsed.type !== "outline" || parsed.count === 0) return label;
  const suffix = ` outline (${parsed.count})`;
  if (label === "Insert at cursor") return `Insert${suffix}`;
  if (label === "Append to end") return `Append${suffix}`;
  if (label === "Replace selection") return `Replace with${suffix}`;
  return label;
}

export function extractReviewChanges(text: string): ReviewChange[] {
  const changes: ReviewChange[] = [];
  // Match ~~old~~ **new** (change) or ~~old~~ (deletion)
  const pattern = /~~([^~\n]+)~~(?:\s*\*\*([^*\n]+)\*\*)?/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    changes.push({
      original: match[1]!.trim(),
      replacement: match[2]?.trim() ?? "",
    });
  }
  return changes;
}
